import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Close as CloseIcon } from "@mui/icons-material";
import { Button, CircularProgress, Drawer, IconButton } from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { formatDate } from "../../utils/portalHelpers";
import { EmptyState, StatusChip } from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerDocumentRequest } from "../../api/endpoints/documents";
import { requestStage, rowProgress, sortRequestsForClient } from "../Documents/documentHelpers";
import DocumentRequestWorkspace from "../Documents/DocumentRequestWorkspace";
import RequestFromClientDialog from "../Documents/RequestFromClientDialog";
import UploadDocumentsDialog from "../Documents/UploadDocumentsDialog";

const FILTERS = [
  { id: "all", label: "All", stages: null },
  { id: "action", label: "Needs action", stages: ["collecting", "ready"] },
  { id: "sent", label: "Sent to office", stages: ["sent"] },
  { id: "cancelled", label: "Cancelled", stages: ["cancelled"] },
];

/** The one thing to do on a request, named for its stage. */
const ACTIONS = {
  ready: { label: "Review & send", variant: "contained" },
  collecting: { label: "Upload files", variant: "outlined" },
  sent: { label: "View", variant: "text" },
  cancelled: { label: "View", variant: "text" },
};

/** Request | received | status | requested | action — fixed so rows line up. */
const GRID =
  "md:grid md:grid-cols-[minmax(0,1fr)_8.5rem_9rem_7rem_8.5rem] md:items-center md:gap-4";

/** A request opened in the side panel: its detail is only fetched once the partner opens it. */
const OpenRequest = ({ requestId, onChanged }) => {
  const {
    data: request,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    partnerQueryKeys.documentRequest(requestId),
    () => fetchPartnerDocumentRequest(requestId),
    { refetchOnMount: true }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <CircularProgress size={22} />
      </div>
    );
  }
  if (isError || !request) {
    return <p className="text-sm text-rose-700 m-0 py-3">Couldn't load this request. Try again.</p>;
  }
  return (
    <DocumentRequestWorkspace
      request={request}
      onChanged={() => {
        refetch();
        onChanged();
      }}
    />
  );
};

/**
 * Every document request on this client, the office's and the partner's own. The partner can
 * upload files they already hold, ask the client through the secure link, and send a finished
 * set to the office — unless the office switched independent requests off for them, in which
 * case they only work the requests routed to them.
 */
const ClientDocumentsPanel = ({ clientId, client, requests = [] }) => {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState(null);
  // Land on what needs doing: partners open this panel to act, not to browse history.
  const [filter, setFilter] = useState("action");
  const [dialog, setDialog] = useState(null);
  const sorted = useMemo(() => sortRequestsForClient(requests), [requests]);
  const canRaise = client?.allowIndependentDocuments !== false;

  const counts = useMemo(() => {
    const byStage = {};
    requests.forEach((request) => {
      const { id } = requestStage(request);
      byStage[id] = (byStage[id] || 0) + 1;
    });
    return Object.fromEntries(
      FILTERS.map((option) => [
        option.id,
        option.stages
          ? option.stages.reduce((sum, stage) => sum + (byStage[stage] || 0), 0)
          : requests.length,
      ])
    );
  }, [requests]);
  const readyCount = useMemo(
    () => requests.filter((request) => requestStage(request).id === "ready").length,
    [requests]
  );
  const activeFilter = FILTERS.find((option) => option.id === filter) || FILTERS[0];
  const visible = activeFilter.stages
    ? sorted.filter((request) => activeFilter.stages.includes(requestStage(request).id))
    : sorted;
  const openRequest = requests.find((request) => request.id === openId) || null;

  const refreshLists = () => {
    queryClient.invalidateQueries(partnerQueryKeys.client(clientId));
    queryClient.invalidateQueries(partnerQueryKeys.documentRequests());
  };

  const onCreated = (created) => {
    refreshLists();
    if (created?.id) setOpenId(created.id);
  };

  const toolbar = canRaise ? (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<UploadFileOutlinedIcon />}
        onClick={() => setDialog("upload")}
      >
        Upload documents
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={<SendOutlinedIcon />}
        onClick={() => setDialog("request")}
      >
        Request from client
      </Button>
    </div>
  ) : (
    <p className="m-0 text-center text-xs text-slate-500">
      The office sends you document requests for this client — raising your own is switched off for
      your account.
    </p>
  );

  const dialogs = (
    <>
      <UploadDocumentsDialog
        clientId={clientId}
        clientName={client?.name}
        open={dialog === "upload"}
        onClose={() => setDialog(null)}
        onCreated={onCreated}
      />
      <RequestFromClientDialog
        clientId={clientId}
        clientName={client?.name}
        clientEmail={client?.email}
        open={dialog === "request"}
        onClose={() => setDialog(null)}
        onCreated={onCreated}
      />
    </>
  );

  if (!requests.length) {
    return (
      <>
        <EmptyState
          title="No documents yet"
          description="Upload files you already have for this client, or send them a secure link listing the documents you need."
          action={toolbar}
        />
        {dialogs}
      </>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-4 py-3.5">
        <div className="min-w-0">
          <h3 className="m-0 text-base font-semibold text-slate-900">Documents</h3>
          <p className="m-0 mt-0.5 text-xs text-slate-500">
            {requests.length} request{requests.length === 1 ? "" : "s"}
            {counts.action ? ` · ${counts.action} need your action` : " · nothing waiting on you"}
            {readyCount ? ` · ${readyCount} ready to send to the office` : ""}
          </p>
        </div>
        {toolbar}
      </header>

      <div className="flex flex-wrap gap-2 border-b border-slate-100 px-4 py-2.5">
        {FILTERS.map((option) => {
          const active = filter === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setFilter(option.id)}
              className={[
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold cursor-pointer transition-colors",
                active
                  ? "border-teal-700 bg-teal-700 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
              ].join(" ")}
            >
              {option.label}
              <span className="tabular-nums opacity-80">{counts[option.id] || 0}</span>
            </button>
          );
        })}
      </div>

      <div
        className={`hidden ${GRID} bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500`}
      >
        <span>Request</span>
        <span>Received</span>
        <span>Status</span>
        <span>Requested</span>
        <span className="text-right">Action</span>
      </div>

      {visible.length ? (
        <ul className="m-0 list-none divide-y divide-slate-100 p-0">
          {visible.map((request) => {
            const stage = requestStage(request);
            const progress = rowProgress(request);
            const action = ACTIONS[stage.id] || ACTIONS.sent;
            const complete = progress.required > 0 && progress.resolved === progress.required;
            // Closed rounds with office-removed files read complete (3/3), not short.
            const doneCount =
              request.status !== "ACTIVE" && progress.removed > 0
                ? progress.resolved
                : progress.fulfilled;
            return (
              <li
                key={request.id}
                className={`${GRID} flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 ${
                  stage.id === "ready" ? "shadow-[inset_3px_0_0_0_#fbbf24]" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(request.id)}
                  className="min-w-0 basis-full border-0 bg-transparent p-0 text-left cursor-pointer md:basis-auto"
                >
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-900 hover:text-teal-800">
                      {request.title}
                    </span>
                    {request.parentRequestId ? <StatusChip tone="rose">Reupload</StatusChip> : null}
                  </span>
                  {request.targetModuleType ? (
                    <span className="block truncate text-xs text-slate-500">
                      Files into {request.targetModuleType}
                    </span>
                  ) : null}
                </button>
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-slate-100 flex">
                    <span
                      className={`block h-full ${complete ? "bg-emerald-500" : "bg-teal-500"}`}
                      style={{ width: `${progress.receivedPercent}%` }}
                    />
                    <span
                      className="block h-full bg-amber-400"
                      style={{ width: `${progress.removedPercent}%` }}
                    />
                  </span>
                  <span className="whitespace-nowrap text-xs tabular-nums text-slate-600">
                    {doneCount}/{progress.required}
                  </span>
                </span>
                <span>
                  <StatusChip tone={stage.tone}>{stage.label}</StatusChip>
                </span>
                <span className="whitespace-nowrap text-xs text-slate-500">
                  {formatDate(request.createdAt)}
                </span>
                <span className="ml-auto md:ml-0 md:text-right">
                  <Button
                    size="small"
                    variant={action.variant}
                    disableElevation
                    color={stage.id === "ready" ? "warning" : "primary"}
                    onClick={() => setOpenId(request.id)}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {action.label}
                  </Button>
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="m-0 px-4 py-8 text-center text-sm text-slate-500">
          Nothing under this filter.
        </p>
      )}

      <Drawer
        anchor="right"
        open={Boolean(openRequest)}
        onClose={() => setOpenId(null)}
        PaperProps={{ sx: { width: { xs: "100vw", sm: 560 } } }}
      >
        {openRequest ? (
          <div className="flex h-full flex-col">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-teal-700">
                  {client?.name || "Client"} · document request
                </p>
                <h3 className="m-0 mt-0.5 truncate text-lg font-bold text-slate-900">
                  {openRequest.title}
                </h3>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <StatusChip tone={requestStage(openRequest).tone}>
                    {requestStage(openRequest).label}
                  </StatusChip>
                  {openRequest.parentRequestId ? (
                    <StatusChip tone="rose">Reupload</StatusChip>
                  ) : null}
                  <span className="text-xs text-slate-500">
                    Requested {formatDate(openRequest.createdAt)}
                  </span>
                </div>
              </div>
              <IconButton onClick={() => setOpenId(null)} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </div>
            <div className="flex-1 overflow-y-auto bg-slate-50 px-5 py-4">
              <OpenRequest requestId={openRequest.id} onChanged={refreshLists} />
            </div>
            <div className="border-t border-slate-200 px-5 py-3 text-right">
              <Link
                to={`/partner/documents/${openRequest.id}`}
                className="text-xs font-semibold text-teal-700 no-underline hover:underline"
              >
                Open as a full page
              </Link>
            </div>
          </div>
        ) : null}
      </Drawer>

      {dialogs}
    </div>
  );
};

export default ClientDocumentsPanel;
